import { ArrowRight, Calendar, Clock, Globe, MessageSquare, Shield, Smartphone, Users, Zap } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useNavigate } from "react-router-dom";

const Features = () => {
  const navigate = useNavigate();
  
  const features = [
    {
      icon: Calendar,
      title: "Shared Calendars",
      description: "Create and manage multiple calendars that can be shared with different groups of people.",
      color: "bg-yellow-100"
    },
    {
      icon: Users,
      title: "Group Events",
      description: "Organize events for different social circles, with easy RSVP tracking and reminders.",
      color: "bg-blue-100"
    },
    {
      icon: Clock,
      title: "Time Zone Support",
      description: "Connect with people anywhere with automatic time zone conversion for all your events.",
      color: "bg-pink-100"
    },
    {
      icon: MessageSquare,
      title: "Event Chat",
      description: "Built-in messaging for each event, making coordination and communication seamless.",
      color: "bg-purple-100"
    },
    {
      icon: Globe,
      title: "Accessible Anywhere",
      description: "Access your calendars from any device with our responsive web application.",
      color: "bg-green-100"
    },
    {
      icon: Shield,
      title: "Privacy Controls",
      description: "Granular privacy settings let you control exactly who can see and edit your events.",
      color: "bg-yellow-100"
    },
    {
      icon: Smartphone,
      title: "Mobile Optimized",
      description: "Designed for both desktop and mobile devices, so you can manage your schedule on the go.",
      color: "bg-blue-100"
    },
    {
      icon: Zap,
      title: "Calendar Sync",
      description: "Sync with Google Calendar and iCloud to keep all your calendars in one place.",
      color: "bg-pink-100"
    }
  ];
  
  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-gradient-to-r from-blue-50 to-indigo-50">
        <div className="container mx-auto max-w-7xl">
          <div className="text-center">
            <h1 className="text-4xl font-bold text-gray-900 mb-6">Powerful Features for Seamless Scheduling</h1>
            <p className="text-lg text-gray-700 max-w-3xl mx-auto mb-8">
              Discover all the tools and capabilities that make fyndtyme the perfect solution for coordinating with friends, family, and colleagues.
            </p>
            <Button 
              size="lg"
              onClick={() => navigate("/calendar")}
              className="bg-blue-600 hover:bg-blue-700 text-white"
            >
              Try Demo Calendar
            </Button>
          </div>
        </div>
      </section>
      
      {/* Features Grid */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="container mx-auto max-w-7xl">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {features.map((feature, index) => (
              <div key={index} className="p-6 border border-gray-200 rounded-lg hover:shadow-md transition-shadow">
                <div className={`${feature.color} p-3 rounded-full w-12 h-12 flex items-center justify-center mb-4`}>
                  <feature.icon className="h-6 w-6 text-gray-900" />
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-3">{feature.title}</h3>
                <p className="text-gray-700">{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>
      
      {/* Feature Highlight: Shared Calendars */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-gray-50">
        <div className="container mx-auto max-w-7xl">
          <div className="flex flex-col lg:flex-row items-center gap-12">
            <div className="lg:w-1/2">
              <h2 className="text-3xl font-bold text-gray-900 mb-6">Shared Calendars Made Simple</h2>
              <p className="text-lg text-gray-700 mb-4">
                Create calendars for different aspects of your life and share them with exactly who you want. 
                Perfect for families, friend groups, teams, and communities.
              </p>
              <ul className="space-y-3 mb-6">
                <li className="flex items-start">
                  <ArrowRight className="h-5 w-5 text-blue-600 mr-2 mt-1 flex-shrink-0" />
                  <span>Create unlimited calendars for different groups</span>
                </li>
                <li className="flex items-start">
                  <ArrowRight className="h-5 w-5 text-blue-600 mr-2 mt-1 flex-shrink-0" />
                  <span>Customize access permissions for viewing and editing</span>
                </li>
                <li className="flex items-start">
                  <ArrowRight className="h-5 w-5 text-blue-600 mr-2 mt-1 flex-shrink-0" />
                  <span>Color-code events for easy visual organization</span>
                </li>
                <li className="flex items-start">
                  <ArrowRight className="h-5 w-5 text-blue-600 mr-2 mt-1 flex-shrink-0" />
                  <span>Receive notifications when events are added or changed</span>
                </li>
              </ul>
              <Button
                onClick={() => navigate("/pricing")}
                className="bg-blue-600 hover:bg-blue-700 text-white flex items-center gap-2"
              >
                View Pricing <ArrowRight className="h-4 w-4" />
              </Button>
            </div>
            <div className="lg:w-1/2 mt-8 lg:mt-0">
              <div className="rounded-lg overflow-hidden shadow-xl border border-gray-200">
                <img 
                  src="/images/dashboard.png" 
                  alt="Shared Calendar Feature" 
                  className="w-full h-auto"
                />
              </div>
            </div>
          </div>
        </div>
      </section>
      
      {/* Feature Highlight: Event Management */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="container mx-auto max-w-7xl">
          <div className="flex flex-col lg:flex-row-reverse items-center gap-12">
            <div className="lg:w-1/2">
              <h2 className="text-3xl font-bold text-gray-900 mb-6">Powerful Event Management</h2>
              <p className="text-lg text-gray-700 mb-4">
                Create detailed events with all the information your attendees need to know.
                Keep everything organized in one place.
              </p>
              <ul className="space-y-3 mb-6">
                <li className="flex items-start">
                  <ArrowRight className="h-5 w-5 text-blue-600 mr-2 mt-1 flex-shrink-0" />
                  <span>Add locations, descriptions, and custom fields</span>
                </li>
                <li className="flex items-start">
                  <ArrowRight className="h-5 w-5 text-blue-600 mr-2 mt-1 flex-shrink-0" />
                  <span>Track RSVPs and attendance</span>
                </li>
                <li className="flex items-start">
                  <ArrowRight className="h-5 w-5 text-blue-600 mr-2 mt-1 flex-shrink-0" />
                  <span>Set up recurring events with flexible patterns</span>
                </li>
                <li className="flex items-start">
                  <ArrowRight className="h-5 w-5 text-blue-600 mr-2 mt-1 flex-shrink-0" />
                  <span>Send automated reminders to all participants</span>
                </li>
              </ul>
              <Button
                onClick={() => navigate("/calendar")}
                className="bg-blue-600 hover:bg-blue-700 text-white flex items-center gap-2"
              >
                Try Demo Calendar <ArrowRight className="h-4 w-4" />
              </Button>
            </div>
            <div className="lg:w-1/2 mt-8 lg:mt-0">
              <div className="rounded-lg overflow-hidden shadow-xl border border-gray-200">
                <img 
                  src="/images/Bookclub.png" 
                  alt="Event Management Feature" 
                  className="w-full h-auto"
                />
              </div>
            </div>
          </div>
        </div>
      </section>
      
      {/* Feature Highlight: Calendar Sync */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-gray-50">
        <div className="container mx-auto max-w-7xl">
          <div className="flex flex-col lg:flex-row items-center gap-12">
            <div className="lg:w-1/2">
              <h2 className="text-3xl font-bold text-gray-900 mb-6">Sync Your Calendar</h2>
              <p className="text-lg text-gray-700 mb-4">
                Keep all your calendars in one place by syncing with Google Calendar and iCloud.
                Never miss an important event again.
              </p>
              <ul className="space-y-3 mb-6">
                <li className="flex items-start">
                  <ArrowRight className="h-5 w-5 text-blue-600 mr-2 mt-1 flex-shrink-0" />
                  <span>Two-way sync with Google Calendar</span>
                </li>
                <li className="flex items-start">
                  <ArrowRight className="h-5 w-5 text-blue-600 mr-2 mt-1 flex-shrink-0" />
                  <span>Import events from iCloud Calendar</span>
                </li>
                <li className="flex items-start">
                  <ArrowRight className="h-5 w-5 text-blue-600 mr-2 mt-1 flex-shrink-0" />
                  <span>Automatic updates across all platforms</span>
                </li>
                <li className="flex items-start">
                  <ArrowRight className="h-5 w-5 text-blue-600 mr-2 mt-1 flex-shrink-0" />
                  <span>Manage all your calendars in one interface</span>
                </li>
              </ul>
              <Button
                onClick={() => navigate("/pricing")}
                className="bg-blue-600 hover:bg-blue-700 text-white flex items-center gap-2"
              >
                Upgrade to Pro <ArrowRight className="h-4 w-4" />
              </Button>
            </div>
            <div className="lg:w-1/2 mt-8 lg:mt-0">
              <div className="rounded-lg overflow-hidden shadow-xl border border-gray-200">
                <img 
                  src="/images/calendar.png" 
                  alt="Calendar Sync Feature" 
                  className="w-full h-auto"
                />
              </div>
            </div>
          </div>
        </div>
      </section>
      
      {/* CTA Section */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-blue-600 text-white">
        <div className="container mx-auto max-w-7xl text-center">
          <h2 className="text-3xl font-bold mb-6">Ready to Experience All Features?</h2>
          <p className="text-xl mb-8 max-w-3xl mx-auto">
            Join thousands of users who are already making scheduling effortless with fyndtime.
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <Button 
              size="lg"
              onClick={() => navigate("/calendar")}
              className="bg-white text-blue-600 hover:bg-gray-100"
            >
              Try Demo Calendar
            </Button>
            <Button 
              size="lg"
              variant="outline"
              onClick={() => navigate("/pricing")}
              className="bg-blue-600 border-white text-white hover:bg-blue-700 border-2 font-medium"
            >
              View Pricing
            </Button>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Features;
